import React, { useState } from "react";

const RectangleEditor = ({
  rect,
  openEditModal,
  startConnecting,
  finishConnecting,
  handleDrag,
  handleCornerDrag,
  fromConnection,
  isSelected,
  onRectangleClick,
  setToNearestTen,
  setDragOverRectangle,
  nightMode,
}) => {
  const [hoveredPointId, setHoveredPointId] = useState(null); // Track hovered circle

  const cornerStyle = { cursor: "crosshair", fill: "blue" }; // Styling for corner points

  return (
    <g
      key={rect.id}
      onMouseDown={(e) => {
        e.stopPropagation();
        let lastX = e.clientX;
        let lastY = e.clientY;

        const onMouseMove = (moveEvent) => {
          const dx = moveEvent.clientX - lastX;
          const dy = moveEvent.clientY - lastY;
          handleDrag(rect.id, dx, dy);
          lastX = moveEvent.clientX;
          lastY = moveEvent.clientY;
        };

        const onMouseUp = () => {
          window.removeEventListener("mousemove", onMouseMove);
          window.removeEventListener("mouseup", onMouseUp);
        };

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
      }}
      onClick={(e) => {
        e.stopPropagation(); // Prevent deselection when clicking on the rectangle
        onRectangleClick();
      }}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {/* Rectangle */}
      <rect
        x={rect.x}
        y={rect.y}
        width={rect.width}
        height={rect.height}
        fill={isSelected ? rect.fillColorSelected : rect.fillColor} // Highlight if selected
        stroke={nightMode ? "#87CEEB" : "#1a1a1a"}
        strokeWidth={isSelected ? 2 : 1}
      />
      {/* Caption */}
      <text
        x={rect.x + rect.width / 2}
        y={rect.y - 5} // Positioned above the rectangle
        fill={rect.textColor}
        textAnchor="middle"
        alignmentBaseline="baseline"
        fontSize="12"
        fontWeight="bold"
        style={{ cursor: "pointer" }}
        onDoubleClick={() => openEditModal(rect)}
      >
        {rect.caption}
      </text>
      {/* Description */}
      <text
        x={rect.x + rect.width / 2}
        y={rect.y + rect.height / 2}
        fill={rect.textColor}
        textAnchor="middle"
        alignmentBaseline="middle"
        fontSize="10"
        style={{ cursor: "pointer" }}
      >
        {rect.description}
      </text>
      {/* Points */}
      {rect.points.map((point, index) => {
        const isCorner =
          (point.x === rect.x && point.y === rect.y) || // Top-left
          (point.x === rect.x + rect.width && point.y === rect.y) || // Top-right
          (point.x === rect.x && point.y === rect.y + rect.height) || // Bottom-left
          (point.x === rect.x + rect.width && point.y === rect.y + rect.height); // Bottom-right

        const isHovered = hoveredPointId === point.id;

        return (
          <g
            key={point.id}
            onMouseEnter={() => {
              setHoveredPointId(point.id);
              if (fromConnection) {
                setDragOverRectangle(rect.id);
              }
            }} // Set hovered state
            onMouseLeave={() => setHoveredPointId(null)} // Reset hovered state
            style={{ cursor: isCorner ? "crosshair" : "pointer" }} // Cursor styling
            onClick={(e) => {
              e.stopPropagation(); // Prevent rectangle click handler
              if (fromConnection) {
                finishConnecting(point.id, rect.id);
              } else {
                startConnecting(point.id, rect.id);
              }
            }}
            onMouseDown={(e) => {
              if (isCorner) {
                e.stopPropagation(); // Prevent rectangle dragging logic
                let lastX = e.clientX;
                let lastY = e.clientY;

                const onMouseMove = (moveEvent) => {
                  const dx = moveEvent.clientX - lastX;
                  const dy = moveEvent.clientY - lastY;
                  handleCornerDrag(
                    rect.id,
                    point.x === rect.x
                      ? point.y === rect.y
                        ? "top-left"
                        : "bottom-left"
                      : point.y === rect.y
                      ? "top-right"
                      : "bottom-right",
                    dx,
                    dy
                  );
                  lastX = moveEvent.clientX;
                  lastY = moveEvent.clientY;
                };

                const onMouseUp = () => {
                  setToNearestTen(
                    point.x === rect.x
                      ? point.y === rect.y
                        ? "top-left"
                        : "bottom-left"
                      : point.y === rect.y
                      ? "top-right"
                      : "bottom-right",
                    rect.id
                  );

                  // Remove event listeners
                  window.removeEventListener("mousemove", onMouseMove);
                  window.removeEventListener("mouseup", onMouseUp);
                };

                window.addEventListener("mousemove", onMouseMove);
                window.addEventListener("mouseup", onMouseUp);
              }
            }}
          >
            {/* Inner Circle - Solid */}
            <circle
              cx={point.x}
              cy={point.y}
              r={2} // Inner solid radius
              fill={isHovered ? "yellow" : "transparent"} // Solid color
            />
            {/* Outer Circle - Transparent */}
            <circle
              cx={point.x}
              cy={point.y}
              r={6} // Outer transparent radius
              fill="transparent" // Transparent fill
              stroke="none" // No stroke for the outer circle
            />
          </g>
        );
      })}
    </g>
  );
};

export default RectangleEditor;
